import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  Input,
  InputLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import FilterBar from "../../components/FilterBar/FilterBar.component";
import { useCityEscapesDatasource } from "../../hooks";
import { useProductsDatasource } from "../../hooks/products.hooks";
import { debounce } from "../../libs/utils";
import { useFilters } from "../../providers/FiltersProvider";
import { GlobalStatsSelection } from "../../types/stats";
import { CityEscapeCalendarChart } from "./CityEscapeCalendarChart";
import { CityEscapeOverview } from "./CityEscapeOverview";
import { CityEscapePlayersNoDistribution } from "./CityEscapePlayersNoDistributionChart";
import CityEscapeTable from "./CityEscapeTable/CityEscapeTable";
import { CityEscapesTrendsOverTimeChart } from "./CityEscapeTrendsOverTimeChart/CityEscapeTrendsOverTimeChart.component";

export interface GlobalStatsSelectorProps {
  selection?: GlobalStatsSelection;
  onChange: (selection: GlobalStatsSelection) => void;
}

interface Option {
  id: string;
  label: string;
  enabled: boolean;
}

export function GlobalStatsSelector({
  selection,
  onChange,
}: GlobalStatsSelectorProps) {
  const cityEscapeDatasource = useCityEscapesDatasource();
  const theme = useTheme();
  const [textFilter, setTextFilter] = useState<string>("");

  const products = useProductsDatasource();

  const [selectionType, setSelectionType] = useState<"story" | "product">(
    "story"
  );

  const handleChange = (options: Option[]) => {
    const selection = options.reduce(
      (acc, option) => (option.enabled ? [...acc, option.id] : acc),
      [] as string[]
    );

    if (selectionType === "story") {
      onChange({
        type: "cityEscape",
        productIds: null,
        cityEscapeIds: selection,
      });
    } else {
      onChange({ type: "product", productIds: selection, cityEscapeIds: null });
    }
  };

  const handleSelection = (options: Option[]) => {
    setOptions(options);
    handleChange(options);
  };

  const handleOptionToggle = (
    id: string | string[],
    { force }: { force?: boolean } = {}
  ) => {
    const newOptions = toggleOptions(Array.isArray(id) ? id : [id], { force });
    handleSelection(newOptions);
  };

  const toggleOptions = (
    ids: string | string[],
    { force = undefined }: { force?: boolean } = {}
  ) => {
    return options.map((option) => {
      if (Array.isArray(ids) ? ids.includes(option.id) : ids === option.id) {
        return { ...option, enabled: force ?? !option.enabled };
      }
      return option;
    });
  };

  const [options, setOptions] = useState<
    { id: string; label: string; enabled: boolean }[]
  >([]);

  const productOptions = useMemo(() => {
    if (products.data) {
      return products.data.map((product) => ({
        id: product.id,
        label: product.title,
        enabled: true,
      }));
    }
    return [];
  }, [products.data]);

  const cityEscapeOptions = useMemo(() => {
    if (cityEscapeDatasource.data) {
      return cityEscapeDatasource.data.map((cityEscape) => ({
        id: cityEscape.id,
        label: cityEscape.label,
        enabled: true,
      }));
    }
    return [];
  }, [cityEscapeDatasource.data]);

  useEffect(() => {
    if (selectionType === "product") {
      handleSelection(productOptions);
    } else {
      handleSelection(cityEscapeOptions);
    }
  }, [selectionType, productOptions, cityEscapeOptions]);

  const filteredOptions = useMemo(() => {
    if (!textFilter) {
      return options;
    }

    return options.filter((option) => {
      const optionTokens = option.label.toLowerCase().split(" ");
      const filterTokens = textFilter.toLowerCase().split(" ");
      return filterTokens.every((token) =>
        optionTokens.some((optToken) => optToken.includes(token))
      );
    });
  }, [options, textFilter]);

  return (
    <FormGroup
      sx={{
        fontSize: "0.9rem",
        maxHeight: "100%",
        overflow: "hidden",
        flexDirection: "column",
        flexWrap: "nowrap",
        height: "100%",
      }}
    >
      <FormControl sx={{ width: "100%" }}>
        <Typography variant="caption" gutterBottom>
          Aggrega per
        </Typography>
        <ToggleButtonGroup
          color="primary"
          exclusive
          aria-label="Platform"
          fullWidth
          size="small"
          value={selectionType}
          onChange={(_, value) => {
            if (value) {
              setSelectionType(value);
            }
          }}
        >
          <ToggleButton value="story">Narrazione</ToggleButton>
          <ToggleButton value="product">Prodotto</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      <FormControl style={{ fontSize: "1em" }}>
        <InputLabel>Cerca</InputLabel>
        <Input
          onInput={debounce((e) => {
            console.debug("Setting text filter to", e.target.value);
            setTextFilter(e.target.value);
          }, 200)}
        />
      </FormControl>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems="center"
      >
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={options.every((m) => m.enabled)}
              indeterminate={
                filteredOptions.some((m) => m.enabled) &&
                !filteredOptions.every((m) => m.enabled)
              }
              onChange={() => {
                handleOptionToggle(
                  filteredOptions.map((m) => m.id),
                  {
                    force: filteredOptions.some((m) => !m.enabled),
                  }
                );
              }}
            />
          }
          label="Includi tutte"
          style={{
            position: "sticky",
            top: 0,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
          }}
          slotProps={{
            typography: {
              fontSize: "0.85em",
            },
          }}
        />
        <Typography fontSize={"1em"}>
          {options.filter((m) => m.enabled).length} Selezionante
        </Typography>
      </Stack>
      <Divider />
      <Stack sx={{ overflowY: "auto", maxHeight: "100%" }}>
        {filteredOptions.map((option) => (
          <FormControlLabel
            key={option.id}
            control={
              <Checkbox
                checked={option.enabled}
                onChange={() => handleOptionToggle(option.id)}
              />
            }
            label={option.label}
            slotProps={{
              typography: {
                fontSize: "1em",
                lineHeight: 1,
              },
            }}
          />
        ))}
      </Stack>
    </FormGroup>
  );
}

const DRAWER_WIDTH = 380;

export function GlobalStatsDrawer({
  children,
  selection,
  onSelectionChange,
}: {
  children?: React.ReactNode;
  selection?: GlobalStatsSelection;
  onSelectionChange?: (selection: GlobalStatsSelection) => void;
}) {
  const [isOpen, setIsOpen] = useState(true);

  const handleSelectionChange = useCallback(
    (newSelection: GlobalStatsSelection) => {
      if (onSelectionChange) {
        onSelectionChange(newSelection);
      }
    },
    [onSelectionChange]
  );

  const toggleDrawer = () => setIsOpen((prev) => !prev);

  return (
    <>
      <Drawer
        id="city-escapes-drawer"
        variant="persistent"
        anchor="right"
        open={isOpen}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: DRAWER_WIDTH,
              top: "unset",
              bottom: 0,
              height: "calc(100% - 72px)",
              overflowY: "hidden",
            },
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="flex-start">
          <IconButton onClick={toggleDrawer} size="small">
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <GlobalStatsSelector
          selection={selection}
          onChange={handleSelectionChange}
        />
      </Drawer>
      <IconButton
        onClick={toggleDrawer}
        size="small"
        sx={{
          position: "fixed",
          right: isOpen ? DRAWER_WIDTH : 0,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1300,
          backgroundColor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: "4px 0 0 4px",
          transition: "right 225ms cubic-bezier(0, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        {isOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>
      <Box
        sx={{
          maxWidth: isOpen ? `calc(100% - ${DRAWER_WIDTH - 14}px)` : "100%",
          paddingRight: isOpen ? 2 : 0,
          transition: "max-width 225ms cubic-bezier(0, 0, 0.2, 1), padding-right 225ms cubic-bezier(0, 0, 0.2, 1)",
        }}
        style={{ overflowY: "auto", maxHeight: "100%" }}
      >
        {children}
      </Box>
    </>
  );
}

export function GlobalStatsPage() {
  const filters = useFilters();
  const [selection, setSelection] = useState<GlobalStatsSelection>({
    type: "cityEscape",
    productIds: null,
    cityEscapeIds: null,
  });
  const cityEscapesDatasource = useCityEscapesDatasource();
  const theme = useTheme();

  const deferredFilters = useDeferredValue(filters.active);
  const deferredSelection = useDeferredValue(selection);

  const onSelectionChange = debounce((newSelection: GlobalStatsSelection) => {
    setSelection(newSelection);
  }, 500);

  const selectionExists = useMemo(
    () => Boolean(selection?.cityEscapeIds || selection?.productIds),
    [selection]
  );

  const pending = useMemo(
    () => !filters.active || !selectionExists,
    [!filters.active, !selectionExists]
  );

  const isChangesPending = useMemo(
    () =>
      pending ||
      deferredSelection !== selection ||
      deferredFilters !== filters.active,
    [pending, deferredSelection, selection, deferredFilters, filters.active]
  );

  if (!cityEscapesDatasource.data) {
    return (
      <Stack height={"100%"} flex={1}>
        <Typography>Caricamento in corso...</Typography>
      </Stack>
    );
  }

  return (
    <GlobalStatsDrawer
      selection={deferredSelection}
      onSelectionChange={onSelectionChange}
    >
      <Stack
        spacing={2}
        style={{
          opacity: isChangesPending ? 0.5 : 1,
        }}
      >
        <FilterBar
          sticky={{ top: 0 }}
          filters={deferredFilters}
          onClearAll={filters.clear}
          onReset={filters.reset}
          onEdit={filters.update}
        />
        <CityEscapeOverview
          selection={deferredSelection}
          filters={deferredFilters}
          pending={isChangesPending}
        />
        <CityEscapeCalendarChart
          selection={deferredSelection}
          filters={deferredFilters}
          pending={isChangesPending}
        />
        <CityEscapesTrendsOverTimeChart
          dataType={deferredSelection.type ?? "cityEscape"}
          selection={deferredSelection}
          filters={deferredFilters}
          opts={{
            defaultAggregationMode: "compare",
            defaultAggregationPeriod: "monthly",
          }}
          pending={isChangesPending}
        />
        <CityEscapePlayersNoDistribution
          selection={deferredSelection}
          filters={deferredFilters}
        />
        <CityEscapeTable
          selection={deferredSelection}
          filters={deferredFilters}
        />
      </Stack>
    </GlobalStatsDrawer>
  );
}

export default GlobalStatsPage;
